import axios from 'axios';

// Use environment variable or detect localhost for development
// In development, use proxy (defined in package.json) which forwards /api to http://localhost:3001
// In production, use relative URL /api which will be handled by the server
const API_BASE_URL = process.env.REACT_APP_API_URL ||
  (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
    ? '/api'
    : '/api');

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests and prevent GET request caching
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  // Prevent browser caching for GET requests
  if (config.method?.toLowerCase() === 'get') {
    config.params = {
      ...config.params,
      _t: new Date().getTime(),
    };
    config.headers['Cache-Control'] = 'no-cache, no-store, must-revalidate';
    config.headers['Pragma'] = 'no-cache';
    config.headers['Expires'] = '0';
  }

  return config;
});

// Handle 401 errors - redirect to login
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Clear token and user data
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      // Redirect to login page
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Helper to get full image URL
export const getImageUrl = (imageUrl?: string | null): string => {
  if (!imageUrl) return '';
  if (imageUrl.startsWith('http')) return imageUrl;

  // Use proxy in development (package.json proxy forwards to localhost:3001)
  // In production, use relative URL
  return imageUrl;
};

export interface User {
  id: string;
  organizationId?: string;
  firstName: string;
  lastName: string;
  mobile: string;
  role: 'superadmin' | 'admin' | 'manager' | 'staff';
  personnelCode?: string;
  gender?: string;
  company?: string;
  cardNumber?: string;
  jobTitle?: string;
  isActive?: boolean;
  workShift?: 'day' | 'night';
  permissions?: string[];
  jobGroupId?: string;

  jobGroupTitle?: string;
  organizationalUnitId?: string;
  organizationalUnitTitle?: string;
  companyId?: string;
  companyTitle?: string;
  avatarUrl?: string;
}

export type MealType = 'breakfast' | 'lunch' | 'dinner';

// Outcome of POST /reservations/scan, sent alongside the human-readable message
export type ScanOutcomeCode =
  | 'DELIVERED'
  | 'GROUP_DELIVERED'
  | 'ALREADY_DELIVERED'
  | 'GROUP_ALREADY_DELIVERED'
  | 'NO_RESERVATION'
  | 'GROUP_NO_RESERVATION'
  | 'NOT_FOUND'
  | 'GROUP_INACTIVE'
  | 'EMPTY'
  | 'TOKEN_DELIVERED'
  | 'TOKEN_NOT_FOUND'
  | 'TOKEN_CANCELLED'
  | 'TOKEN_WRONG_DAY'
  | 'TOKEN_WRONG_MEAL';

export interface LookupReservation {
  id: string;
  mealType: MealType;
  mealDate: string;
  isDelivered: boolean;
  deliveredAt: string | null;
  isCancelled: boolean;
  cancelledAt: string | null;
  isCorporate: boolean;
  createdAt: string;
  meal: { title: string; imageUrl?: string | null };
}

export interface LookupUser {
  id: string;
  firstName: string;
  lastName: string;
  personnelCode?: string | null;
  cardNumber?: string | null;
  hasCardSerial: boolean;
  isActive: boolean;
  role: string;
  company?: string | null;
  unitTitle?: string | null;
  today: LookupReservation[];
  upcoming: LookupReservation[];
  matched: LookupReservation[]; // the reservation a scanned guest token belongs to
}

export interface ReservationLookupResult {
  query: string;
  date: string;
  matchType: 'group' | 'token' | 'exact' | 'partial' | 'name' | 'none';
  matchedReservationId: string | null;
  group: { id: string; title: string; isActive: boolean; memberCount: number } | null;
  truncated: boolean;
  users: LookupUser[];
}

// One printable guest meal token; its barcode delivers exactly that meal at the monitor
export interface GuestToken {
  id: string;
  deliveryCode: string | null;
  mealDate: string;
  mealType: MealType;
  mealTitle: string;
  mealImage?: string | null;
  unitTitle: string;
  description?: string | null;
  isCancelled: boolean;
  isDelivered: boolean;
  deliveredAt: string | null;
  createdAt: string;
}

export interface GuestTokensResult {
  restaurant: { name: string; logoUrl: string | null };
  tokens: GuestToken[];
}

export interface OrganizationalUnit {
  id: string;
  title: string;
  companyId?: string | null;
  companyTitle?: string | null;
  externalcode?: string | null;
}

export interface Company {
  id: string;
  title: string;
}

export interface JobGroup {
  id: string;
  title: string;
  reservationLimit: number;
  cancellationLimit?: number | null;
  isActive: boolean;
}

export interface Meal {
  id: string;
  title: string;
  description?: string;
  price: number;
  staffShare: number;
  imageUrl?: string;
  ingredients?: string[]; // Array of ingredient names
  calories?: number;
  category?: 'food' | 'salad' | 'breakfast' | 'beverage' | 'other';
  averageRating?: number | null;
  ratingCount?: number;
}

export interface MenuItem {
  id: string;
  mealId: string;
  meal: Meal;
  date: string;
  mealType: 'breakfast' | 'lunch' | 'dinner';
  maxReservations?: number;
  currentReservations?: number;
  isCorporate: boolean;
  isActive?: boolean;
  price?: number | null;
  corporatePrice?: number | null;
}

export interface Reservation {
  id: string;
  userId: string;
  menuItemId: string;
  mealId: string; // Added mealId based on instruction interpretation
  mealDate: string;
  mealType: 'breakfast' | 'lunch' | 'dinner'; // Updated mealType based on instruction
  createdAt: string; // Added createdAt based on instruction
  isCancelled: boolean;
  cancelledAt?: string;
}

// Auth API
export const authAPI = {
  login: async (mobile: string, password: string, role: 'superadmin' | 'admin' | 'manager') => {
    const response = await api.post('/auth/login', { mobile, password, role });
    if (response.data.token) {
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
    }
    return response.data;
  },
  register: async (mobile: string, password: string, organizationName: string) => {
    const response = await api.post('/auth/register', { mobile, password, organizationName });
    return response.data;
  },
  logout: () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  },
  getCurrentUser: (): User | null => {
    const userStr = localStorage.getItem('user');
    return userStr ? JSON.parse(userStr) : null;
  },
  impersonate: async (targetOrganizationId: string) => {
    const response = await api.post('/auth/impersonate', { target_organization_id: targetOrganizationId });
    if (response.data.token) {
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
    }
    return response.data;
  }
};

export interface Organization {
  id: string;
  name: string;
  slug: string;
  domain?: string | null;
  is_active: boolean;
  expires_at?: string;
  created_at: string;
  total_users?: number;
  active_staff?: number;
  total_admins?: number;
  total_meals?: number;
  total_reservations?: number;
  total_companies?: number;
  primary_admin_name?: string;
  primary_admin_mobile?: string;
  logo_url?: string;
  sync_db_type?: string;
  sync_cron_enabled?: boolean;
}

// Organizations API
export const organizationsAPI = {
  getOrganizations: async () => {
    const response = await api.get('/organizations');
    return response.data;
  },
  createOrganization: async (data: Partial<Organization>) => {
    const response = await api.post('/organizations', data);
    return response.data;
  },
  updateOrganization: async (id: string, data: Partial<Organization>) => {
    const response = await api.put(`/organizations/${id}`, data);
    return response.data;
  },
  toggleActive: async (id: string) => {
    const response = await api.patch(`/organizations/${id}/toggle-status`);
    return response.data;
  },
  deleteOrganization: async (id: string) => {
    const response = await api.delete(`/organizations/${id}`);
    return response.data;
  }
};

// Users API
export const usersAPI = {
  getUsers: async (role?: 'superadmin' | 'admin' | 'manager' | 'staff' | string) => {
    const params = role ? { role } : {};
    const response = await api.get('/users', { params });
    return response.data;
  },
  createUser: async (userData: Partial<User> & { password?: string }) => {
    const response = await api.post('/users', userData);
    return response.data;
  },
  updateUser: async (userId: string, userData: Partial<User> & { password?: string }) => {
    const response = await api.put(`/users/${userId}`, userData);
    return response.data;
  },
  deleteUser: async (userId: string) => {
    const response = await api.delete(`/users/${userId}`);
    return response.data;
  },
  toggleUserActive: async (userId: string) => {
    const response = await api.patch(`/users/${userId}/toggle-active`);
    return response.data;
  },
  bulkUpload: async (users: Array<{
    personnelCode: string;
    firstName: string;
    lastName: string;
    mobile: string;
    jobTitle?: string;
    gender?: string;
    company?: string;
    cardNumber?: string;
  }>) => {
    const response = await api.post('/users/bulk', { users });
    return response.data;
  },
  uploadAvatar: async (imageFile: File | Blob, userId?: string): Promise<string> => {
    const formData = new FormData();
    const filename = imageFile instanceof File ? imageFile.name : 'avatar.jpg';
    formData.append('avatar', imageFile, filename);
    if (userId) {
      formData.append('userId', userId);
    }

    const token = localStorage.getItem('token');
    const response = await fetch('/api/users/upload-avatar', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: formData,
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'خطا در آپلود تصویر پروفایل');
    }

    const data = await response.json();
    return data.avatarUrl;
  },
};

// Meals API
export const mealsAPI = {
  getMeals: async () => {
    const response = await api.get('/meals');
    if (Array.isArray(response.data)) {
      return response.data.map((meal: any) => ({
        ...meal,
        price: meal.price != null ? meal.price / 10 : meal.price,
        staffShare: meal.staffShare != null ? meal.staffShare / 10 : meal.staffShare
      }));
    }
    return response.data;
  },
  createMeal: async (mealData: Partial<Meal>) => {
    const dataToSend = { ...mealData };
    if (dataToSend.price != null) dataToSend.price = dataToSend.price * 10;
    if (dataToSend.staffShare != null) dataToSend.staffShare = dataToSend.staffShare * 10;
    const response = await api.post('/meals', dataToSend);
    return response.data;
  },
  updateMeal: async (mealId: string, mealData: Partial<Meal>) => {
    const dataToSend = { ...mealData };
    if (dataToSend.price != null) dataToSend.price = dataToSend.price * 10;
    if (dataToSend.staffShare != null) dataToSend.staffShare = dataToSend.staffShare * 10;
    const response = await api.put(`/meals/${mealId}`, dataToSend);
    return response.data;
  },
  deleteMeal: async (mealId: string) => {
    const response = await api.delete(`/meals/${mealId}`);
    return response.data;
  },
  uploadImage: async (imageFile: File | Blob, mealId?: string): Promise<string> => {
    const formData = new FormData();
    const filename = imageFile instanceof File ? imageFile.name : 'meal-image.jpg';
    formData.append('image', imageFile, filename);
    if (mealId) {
      formData.append('mealId', mealId);
    }

    const token = localStorage.getItem('token');
    const response = await fetch('/api/meals/upload-image', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: formData,
    });

    if (!response.ok) {
      throw new Error('خطا در آپلود تصویر');
    }

    const data = await response.json();
    return data.imageUrl;
  },
};

// Menu Items API
export const menuItemsAPI = {
  getMenuItems: async (startDate?: string, endDate?: string) => {
    const params: any = {};
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    const response = await api.get('/menu', { params });
    if (Array.isArray(response.data)) {
      return response.data.map((item: any) => {
        const mapped = { ...item };
        if (mapped.price != null) mapped.price = mapped.price / 10;
        if (mapped.corporatePrice != null) mapped.corporatePrice = mapped.corporatePrice / 10;
        if (mapped.meal) {
          mapped.meal = { ...mapped.meal };
          if (mapped.meal.price != null) mapped.meal.price = mapped.meal.price / 10;
          if (mapped.meal.staffShare != null) mapped.meal.staffShare = mapped.meal.staffShare / 10;
        }
        return mapped;
      });
    }
    return response.data;
  },
  createMenuItem: async (menuItemData: Partial<MenuItem>) => {
    const dataToSend = { ...menuItemData };
    if (dataToSend.price != null) dataToSend.price = dataToSend.price * 10;
    if (dataToSend.corporatePrice != null) dataToSend.corporatePrice = dataToSend.corporatePrice * 10;
    const response = await api.post('/menu', dataToSend);
    return response.data;
  },
  updateMenuItem: async (menuItemId: string, menuItemData: Partial<MenuItem>) => {
    const dataToSend = { ...menuItemData };
    if (dataToSend.price != null) dataToSend.price = dataToSend.price * 10;
    if (dataToSend.corporatePrice != null) dataToSend.corporatePrice = dataToSend.corporatePrice * 10;
    const response = await api.put(`/menu/${menuItemId}`, dataToSend);
    return response.data;
  },
  deleteMenuItem: async (menuItemId: string) => {
    // Backend doesn't have delete endpoint, we'll need to add it or use a workaround
    const response = await api.delete(`/menu/${menuItemId}`);
    return response.data;
  },
};

// Reservations API
export const reservationsAPI = {
  getReservations: async (userId?: string) => {
    const params = userId ? { userId } : {};
    const response = await api.get('/reservations', { params });
    return response.data;
  },
  getDailyReservations: async (startDate: string, endDate: string, mealType?: 'all' | 'breakfast' | 'lunch' | 'dinner') => {
    const params: any = { startDate, endDate };
    if (mealType && mealType !== 'all') params.mealType = mealType;
    const response = await api.get('/reservations/daily', { params });
    return response.data;
  },
  getTodayStats: async (mealType?: string) => {
    const params: any = {};
    if (mealType) params.mealType = mealType;
    const response = await api.get('/reservations/stats/today', { params });
    return response.data;
  },
  getMonthlyStats: async (startDate: string, endDate: string) => {
    const response = await api.get('/reservations/stats/monthly', { params: { startDate, endDate } });
    return response.data;
  },
  scanCard: async (cardNumber: string, mealType?: string) => {
    const payload: any = { cardNumber };
    if (mealType) payload.mealType = mealType;
    const response = await api.post('/reservations/scan', payload);
    return response.data;
  },
  assignCardSerial: async (userId: string, cardSerial: string) => {
    const response = await api.post('/reservations/assign-card-serial', { userId, cardSerial });
    return response.data;
  },
  // Read-only: never delivers. q may be a card, reader serial, personnel code or a name.
  lookupReservations: async (q: string, date?: string): Promise<ReservationLookupResult> => {
    const response = await api.get('/reservations/lookup', { params: date ? { q, date } : { q } });
    return response.data;
  },
  // Marks today's delivered reservations as undelivered again (restaurant monitor)
  undoDelivery: async (reservationIds: string[]): Promise<{ undone: number; skipped: number; reservationIds: string[]; message: string }> => {
    const response = await api.post('/reservations/undo-delivery', { reservationIds });
    return response.data;
  },
  getGuestTokens: async (reservationIds: string[]): Promise<GuestTokensResult> => {
    const response = await api.post('/reservations/guest-tokens', { reservationIds });
    return response.data;
  },
  createReservation: async (data: { userId: string, mealId: string, mealDate: string, mealType: string, isCorporate?: boolean }) => {
    const response = await api.post('/reservations', data);
    return response.data;
  },
  createGuestReservations: async (data: { organizationalUnitId: string, mealId: string, mealDate: string, mealType: string, quantity: number, description?: string }): Promise<{ message: string; reservations?: { id: string; deliveryCode: string }[] }> => {
    const response = await api.post('/reservations/guest', data);
    return response.data;
  },
  getGuestReservations: async () => {
    const response = await api.get('/reservations/guest-list');
    return response.data;
  },
  cancelReservation: async (id: string) => {
    const response = await api.post(`/reservations/${id}/cancel`);
    return response.data;
  },
};

// Job Groups API
export const jobGroupsAPI = {
  getJobGroups: async () => {
    const response = await api.get('/job-groups');
    return response.data;
  },
  createJobGroup: async (data: Partial<JobGroup>) => {
    const response = await api.post('/job-groups', data);
    return response.data;
  },
  updateJobGroup: async (id: string, data: Partial<JobGroup>) => {
    const response = await api.put(`/job-groups/${id}`, data);
    return response.data;
  },
  deleteJobGroup: async (id: string) => {
    const response = await api.delete(`/job-groups/${id}`);
    return response.data;
  },
};

// Organizational Units API
export const organizationalUnitsAPI = {
  getOrganizationalUnits: async () => {
    const response = await api.get('/organizational-units');
    return response.data;
  },
  createOrganizationalUnit: async (data: Partial<OrganizationalUnit>) => {
    const response = await api.post('/organizational-units', data);
    return response.data;
  },
  updateOrganizationalUnit: async (id: string, data: Partial<OrganizationalUnit>) => {
    const response = await api.put(`/organizational-units/${id}`, data);
    return response.data;
  },
  deleteOrganizationalUnit: async (id: string) => {
    const response = await api.delete(`/organizational-units/${id}`);
    return response.data;
  },
};

// Companies API
export const companiesAPI = {
  getCompanies: async () => {
    const response = await api.get('/companies');
    return response.data;
  },
  createCompany: async (data: Partial<Company>) => {
    const response = await api.post('/companies', data);
    return response.data;
  },
  updateCompany: async (id: string, data: Partial<Company>) => {
    const response = await api.put(`/companies/${id}`, data);
    return response.data;
  },
  deleteCompany: async (id: string) => {
    const response = await api.delete(`/companies/${id}`);
    return response.data;
  },
};

export interface SyncConfig {
  host?: string;
  port?: number | string;
  username?: string;
  password?: string;
  database?: string;
  tableName?: string;
  dbType?: 'mssql' | 'mysql' | 'postgres' | string;
  fieldMap?: {
    personnelCode: string;
    firstName: string;
    lastName: string;
    mobile: string;
    jobTitle: string;
    gender: string;
    company: string;
    cardNumber: string;
    isActive: string;
    unitCode?: string;
    unitTitle?: string;
  };
  cronEnabled?: boolean;
}

// MS SQL Sync API
export const syncAPI = {
  getConfig: async () => {
    const response = await api.get('/sync/config');
    return response.data;
  },
  updateConfig: async (data: SyncConfig) => {
    const response = await api.put('/sync/config', data);
    return response.data;
  },
  executeManualSync: async () => {
    const response = await api.post('/sync/execute');
    return response.data;
  },
  checkConnection: async (config: SyncConfig) => {
    const response = await api.post('/sync/check-connection', config);
    return response.data;
  }
};

export interface SystemSettings {
  systemEnabled: boolean;
  appEnabled: boolean;
  restaurantName?: string;
  logoUrl?: string;
  freeFoodTimeLimitOverride?: boolean;
  breakfastStartTime?: string;
  lunchStartTime?: string;
  dinnerStartTime?: string;
  excelCurrency?: 'toman' | 'rial';
}

export const systemSettingsAPI = {
  getSystemSettings: async (): Promise<SystemSettings> => {
    const response = await api.get('/settings');
    return response.data;
  },
  updateSystemSettings: async (data: Partial<SystemSettings>): Promise<SystemSettings> => {
    const response = await api.put('/settings', data);
    return response.data;
  },
  getPublicSettings: async (organizationId?: string): Promise<{ restaurantName: string, logoUrl: string, breakfastStartTime?: string, lunchStartTime?: string, dinnerStartTime?: string }> => {
    const params = organizationId ? { organizationId } : {};
    const response = await api.get('/settings/public', { params });
    return response.data;
  },
  uploadLogo: async (imageFile: File | Blob): Promise<string> => {
    const formData = new FormData();
    const filename = imageFile instanceof File ? imageFile.name : 'logo.png';
    formData.append('logo', imageFile, filename);

    const token = localStorage.getItem('token');
    const response = await fetch('/api/settings/upload-logo', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: formData,
    });

    if (!response.ok) {
      throw new Error('خطا در آپلود لوگو');
    }

    const data = await response.json();
    return data.url;
  },
  getHolidays: async (): Promise<Record<string, {title: string, isHoliday: boolean}[]>> => {
    try {
      const response = await api.get('/settings/public/holidays');
      return response.data || {};
    } catch (e) {
      return {};
    }
  }
};

export interface SystemStats {
  organizations: {
    total: number;
    active: number;
    inactive: number;
  };
  users: {
    total: number;
    admins: number;
    managers: number;
    staff: number;
    superadmins: number;
  };
  reservations: {
    today: number;
  };
  recentOrganizations: {
    id: string;
    name: string;
    created_at: string;
    is_active: boolean;
  }[];
}

export const superAdminAPI = {
  getSystemStats: async (): Promise<SystemStats> => {
    const response = await api.get('/super-admin/stats');
    return response.data;
  },
  uploadHolidays: async (formData: FormData): Promise<{ message: string; count: number }> => {
    const token = localStorage.getItem('token');
    const response = await fetch('/api/super-admin/upload-holidays', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: formData,
    });
    if (!response.ok) {
      throw new Error('خطا در آپلود تقویم');
    }
    return response.json();
  }
};

export interface FinanceReportItem {
  user_id: string;
  personnelcode: string;
  firstname: string;
  lastname: string;
  total_reservations: number;
  free_food_count: number;
  free_food_amount: number;
  org_food_count: number;
  org_food_staff_amount: number;
  org_food_corp_amount: number;
  staff_total_amount: number;
  org_total_amount: number;
  grand_total: number;
}

export const reportsAPI = {
  getMonthlyFinanceReport: async (startDate: string, endDate: string): Promise<FinanceReportItem[]> => {
    const response = await api.get('/reports/monthly-finance', {
      params: { startDate, endDate }
    });
    if (Array.isArray(response.data)) {
      return response.data.map((item: any) => ({
        ...item,
        free_food_amount: item.free_food_amount != null ? Number(item.free_food_amount) / 10 : item.free_food_amount,
        org_food_staff_amount: item.org_food_staff_amount != null ? Number(item.org_food_staff_amount) / 10 : item.org_food_staff_amount,
        org_food_corp_amount: item.org_food_corp_amount != null ? Number(item.org_food_corp_amount) / 10 : item.org_food_corp_amount,
        staff_total_amount: item.staff_total_amount != null ? Number(item.staff_total_amount) / 10 : item.staff_total_amount,
        org_total_amount: item.org_total_amount != null ? Number(item.org_total_amount) / 10 : item.org_total_amount,
        grand_total: item.grand_total != null ? Number(item.grand_total) / 10 : item.grand_total,
      }));
    }
    return [];
  },
  getDailyPredictionReport: async (startDate: string, endDate: string) => {
    const response = await api.get('/reports/daily-prediction', {
      params: { startDate, endDate }
    });
    return response.data;
  },
  getMealReservationsReport: async (startDate: string, endDate: string): Promise<MealReservationsReportItem[]> => {
    const response = await api.get('/reports/meal-reservations', {
      params: { startDate, endDate }
    });
    if (Array.isArray(response.data)) {
      return response.data.map((item: any) => ({
        ...item,
        total_reservations: Number(item.total_reservations) || 0,
        org_count: Number(item.org_count) || 0,
        guest_count: Number(item.guest_count) || 0,
        total_price: item.total_price != null ? Number(item.total_price) / 10 : item.total_price, // convert to Toman
      }));
    }
    return [];
  },
  getUnitServedMealsReport: async (startDate: string, endDate: string): Promise<UnitServedMealsReportItem[]> => {
    const response = await api.get('/reports/unit-served-meals', {
      params: { startDate, endDate }
    });
    if (Array.isArray(response.data)) {
      return response.data.map((item: any) => ({
        ...item,
        total_served: Number(item.total_served) || 0,
        org_served_count: Number(item.org_served_count) || 0,
        guest_served_count: Number(item.guest_served_count) || 0,
        total_price: item.total_price != null ? Number(item.total_price) / 10 : item.total_price, // convert to Toman
        staff_total_amount: item.staff_total_amount != null ? Number(item.staff_total_amount) / 10 : item.staff_total_amount,
        org_total_amount: item.org_total_amount != null ? Number(item.org_total_amount) / 10 : item.org_total_amount,
      }));
    }
    return [];
  },
  getMealRatingsReport: async (startDate: string, endDate: string): Promise<MealRatingsReportItem[]> => {
    const response = await api.get('/reports/meal-ratings', {
      params: { startDate, endDate }
    });
    if (Array.isArray(response.data)) {
      return response.data.map((item: any) => ({
        ...item,
        average_rating: Number(item.average_rating) || 0,
        total_ratings: Number(item.total_ratings) || 0,
      }));
    }
    return [];
  }
};

export interface MealReservationsReportItem {
  meal_title: string;
  meal_image?: string;
  total_reservations: number;
  org_count: number;
  guest_count: number;
  total_price: number;
}

export interface UnitServedMealsReportItem {
  unit_title: string | null;
  meal_title: string;
  meal_image?: string;
  total_served: number;
  org_served_count: number;
  guest_served_count: number;
  total_price: number;
  staff_total_amount: number;
  org_total_amount: number;
}

export default api;

export interface MealRatingsReportItem {
  meal_id: string;
  meal_title: string;
  meal_image?: string;
  average_rating: number;
  total_ratings: number;
}

export interface DeliveryGroup {
  id: string;
  title: string;
  master_card_number: string;
  is_active: boolean;
  member_count?: number;
  members?: User[];
}

export const deliveryGroupsAPI = {
  getGroups: async () => {
    const response = await api.get('/delivery-groups');
    return response.data;
  },
  getGroupById: async (id: string) => {
    const response = await api.get(`/delivery-groups/${id}`);
    return response.data;
  },
  createGroup: async (data: Partial<DeliveryGroup>) => {
    const response = await api.post('/delivery-groups', data);
    return response.data;
  },
  updateGroup: async (id: string, data: Partial<DeliveryGroup>) => {
    const response = await api.put(`/delivery-groups/${id}`, data);
    return response.data;
  },
  deleteGroup: async (id: string) => {
    const response = await api.delete(`/delivery-groups/${id}`);
    return response.data;
  },
  addMember: async (id: string, user_id: string) => {
    const response = await api.post(`/delivery-groups/${id}/members`, { user_id });
    return response.data;
  },
  removeMember: async (id: string, user_id: string) => {
    const response = await api.delete(`/delivery-groups/${id}/members/${user_id}`);
    return response.data;
  }
};
